import { useEffect, useMemo, useState } from "react";
import { AppDialog } from "@/components/common";
import { SearchableSelect } from "@/components/common/SearchableSelect";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTran } from "@/hooks";
import { showToast } from "@/lib/utils/toast";
import { generateYearOptions } from "../../utils/year-helpers";
import { Building2 } from "lucide-react";
import { Users } from "lucide-react";
import { OrgTreeNodeBreadcrumb } from "@/components/common/OrgTreeNodeBreadcrumb";
import {
  formatMonth,
  formatReportingYearPeriod,
} from "../../utils/period-constants";

const ManageMasterDataDialog = ({
  open,
  onOpenChange,
  row,
  organizationTree = [],
  organizationLoading = false,
  onSave,
  company,
  selectedReportingYear,
  reportingYearOrganizationIds,
}) => {
  const { t } = useTran();
  const generalOnly = row?.disclosureCategory === "General";
  const [allOrganizations, setAllOrganizations] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [initialOrganizations, setInitialOrganizations] = useState([]);
  const [initialAllOrganizations, setInitialAllOrganizations] = useState(true);
  // inside your component
  const [year, setYear] = useState(
    row?.reportingYear?.toString() || String(new Date().getFullYear()),
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !row) return;

    const initialAllOrg = Boolean(row.isAllOrganization);

    setAllOrganizations(initialAllOrg);
    setInitialAllOrganizations(initialAllOrg);

    setYear(String(row.reportingYear));

    let baseIds = Array.isArray(row.organizationIds)
      ? row.organizationIds
      : Array.isArray(row.organizations)
        ? row.organizations
        : [];

    if (row.isCompanySelected && company?.id) {
      baseIds = Array.from(new Set([...baseIds, company.id]));
    }

    setOrganizations(baseIds);
    setInitialOrganizations(baseIds);
  }, [open, row, company]);

  const isOrganizationChanged = useMemo(() => {
    const current = [...organizations].map(String).sort();
    const initial = [...initialOrganizations].map(String).sort();

    return (
      JSON.stringify(current) !== JSON.stringify(initial) ||
      allOrganizations !== initialAllOrganizations
    );
  }, [
    organizations,
    initialOrganizations,
    allOrganizations,
    initialAllOrganizations,
  ]);

  return (
    <AppDialog
      open={open}
      onOpenChange={(openState) => {
        if (!openState) onOpenChange(false);
      }}
      isDirty={isOrganizationChanged}
      title={t("Manage Metric Assignment")}
      description={t("Update organization scope for this disclosure.")}
      size="lg"
      className="rounded-xl shadow-lg"
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="px-3 py-1 text-sm"
          >
            {t("Close")}
          </Button>
          <Button
            type="button"
            disabled={
              !isOrganizationChanged ||
              saving ||
              (!allOrganizations && organizations.length === 0)
            }
            onClick={async () => {
              if (!row) return;

              if (!allOrganizations && organizations.length === 0) {
                showToast.error(t("Please select at least one organization."));
                return;
              }
              const parsedYear = Number.parseInt(year, 10);
              if (!Number.isFinite(parsedYear)) {
                showToast.error(t("Please select a valid year."));
                return;
              }

              setSaving(true);
              try {
                await onSave?.({
                  disclosureId: row.id,
                  year: parsedYear,
                  isAllOrganization: allOrganizations,
                  organizationIds: allOrganizations ? [] : organizations,
                });
                onOpenChange(false);
              } finally {
                setSaving(false);
              }
            }}
            className="px-4 py-1 text-sm"
          >
            {t("Save Changes")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 mx-4 ">
        {" "}
        {/* reduced spacing and padding */}
        {/* Disclosure Info */}
        <div className="rounded-xl border border-border bg-muted/30 p-4 ">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("Disclosure Information")}
            </p>

            {row?.disclosureCategory && (
              <Badge variant="outline" className="bg-background">
                {row.disclosureCategory}
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Code */}
            <div>
              <p className="text-xs text-muted-foreground">{t("Code")}</p>
              <p className="mt-1 text-sm font-semibold text-foreground break-all">
                {row?.disclosureCode || "-"}
              </p>
            </div>

            {/* Framework */}
            <div>
              <p className="text-xs text-muted-foreground">{t("Framework")}</p>
              <p className="mt-1 text-sm font-semibold text-foreground break-all">
                {row?.framework || "-"}
              </p>
            </div>

            {/* Year */}
            <div>
              <p className="text-xs text-muted-foreground">{t("Year")}</p>
              <p className="mt-1 text-sm font-semibold text-foreground">
                {row?.reportingYear || "-"}
              </p>
            </div>

            {/* Title */}
            <div>
              <p className="text-xs text-muted-foreground">{t("Title")}</p>
              <p className="mt-1 text-sm font-semibold break-words whitespace-pre-wrap">
                {row?.disclosureTitle || "-"}
              </p>
            </div>
          </div>
        </div>
        {/* Year */}
        <div className="flex flex-col">
          <p className="text-xs font-medium text-foreground whitespace-nowrap py-1">
            {t("Reporting Year")}
            {/* {t("Reporting Year")} <span className="text-red-500">*</span> */}
          </p>
          <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-foreground">
            {selectedReportingYear ? (
              <>
                <span>{selectedReportingYear.startYear}</span>
                {" ("}
                <span>
                  {formatMonth(selectedReportingYear.startMonth)}
                </span>{" "}
                <span>{selectedReportingYear.startYear}</span>
                <span className="mx-2 text-muted-foreground">-</span>
                <span>{formatMonth(selectedReportingYear.endMonth)}</span>{" "}
                <span>{selectedReportingYear.endYear}</span>
                {")"}
              </>
            ) : (
              <span className="text-muted-foreground">
                {t("No reporting year selected")}
              </span>
            )}
          </div>
        </div>
        {/* Organization Scope */}
        <div>
          <p className="text-xs font-medium text-foreground py-1">
            {t("Organization Scope")}
            {generalOnly && (
              <span className="ml-2 text-muted-foreground font-normal">
                ({t("General disclosures are assigned to Parent Company only")})
              </span>
            )}
          </p>
          {generalOnly ? (
            <p className="text-sm font-semibold text-primary flex items-center gap-1.5">
              <Building2 className="h-4 w-4" />
              {company?.name ||
                organizationTree?.[0]?.name ||
                t("Parent Company")}
            </p>
          ) : (
            <div className="w-fit rounded-full border border-border p-1">
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant={allOrganizations ? "secondary" : "ghost"}
                  className="h-8 text-xs rounded-full flex items-center gap-1 px-3"
                  disabled={!selectedReportingYear?.isAllOrganization}
                  onClick={() => {
                    setAllOrganizations(true);
                    setOrganizations([]);
                  }}
                >
                  <Building2 className="h-3.5 w-3.5" />
                  {t("All Organizations")}
                </Button>
                <Button
                  type="button"
                  variant={!allOrganizations ? "secondary" : "ghost"}
                  className="h-8 text-xs rounded-full flex items-center gap-1 px-3"
                  disabled={!selectedReportingYear?.isAllOrganization}
                  onClick={() => {
                    setAllOrganizations(false);
                    setOrganizations([]);
                  }}
                >
                  <Users className="h-3.5 w-3.5" />
                  {t("Specific Organization")}
                </Button>
              </div>
            </div>
          )}
          {!generalOnly && (
            <span className="ml-2 text-muted-foreground text-xs">
              (
              {`${selectedReportingYear?.isAllOrganization ? "All" : reportingYearOrganizationIds.length} organizations are assigned to selected reporting year`}
              )
            </span>
          )}
        </div>
        {/* Organization Multi-Select */}
        {!generalOnly && !allOrganizations && (
          <div>
            <p className="text-xs font-medium text-foreground p-1">
              {t("Organizations")} <span className="text-red-500">*</span>
            </p>
            <OrgTreeNodeBreadcrumb
              includeParentCompany={true}
              tree={organizationTree}
              value={organizations}
              onChange={setOrganizations}
              placeholder={t("Select organizations")}
              disabled={organizationLoading || saving}
              allowOrgs={reportingYearOrganizationIds}
            />
          </div>
        )}
      </div>
    </AppDialog>
  );
};

export default ManageMasterDataDialog;
